import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Banknote,
  CircleDot,
  FileText,
  HandCoins,
  Receipt,
  RefreshCw,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  Button,
  FilterBar,
  SearchField,
  type IconTileTone,
} from "@/components/ui";
import {
  PageFrame,
  StatCard,
  StatCardGrid,
  selectableStatCardButtonClass,
} from "@/components/common";
import {
  DataTable,
  DataTablePrimaryText,
  queryLoadState,
  type DataTableColumn,
} from "@/components/table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  pageSkip,
  serverPageWindow,
  useClampUrlPage,
  useUrlPage,
} from "@/hooks/useUrlPage";
import {
  adminSettlementKeys,
  adminSettlementService,
  type SettlementPayeeRow,
  type SettlementPayeeTotals,
} from "@/services/adminSettlementService";
import { UserRole } from "@/types";
import {
  settlementPayeeStatusParams,
  settlementRoleLabel,
  type SettlementPayeeListStatus,
} from "./settlementUi";
import { formatMoney, formatMoneyOrEmpty } from "@/lib/money";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import {
  AdminRegionFilter,
  type AdminRegionFilterValue,
} from "@/components/admin/AdminRegionFilter";
import { RegionCodeBadge } from "@/components/admin/RegionCodeBadge";
import { ROLE_ICON_CONFIG } from "@/components/admin/users/roleIconConfig";
import { LegalIncompleteBadge } from "./LegalIncompleteBadge";
import { getInitials } from "@/lib/utils";

type RoleFilter =
  | ""
  | typeof UserRole.SELLER
  | typeof UserRole.HOST
  | typeof UserRole.SALES_AGENT;

/**
 * Rows arrive sorted by name from the server; columns are not client-sortable because
 * sorting one page would misrepresent the full list.
 */
type ServerOrderColumn = "name";

const PAGE_SIZE = 50;

export default function SettlementsPayeesPage() {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const navigate = useNavigate();
  const [page, setPage] = useUrlPage();
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("");
  const [statusFilter, setStatusFilter] = useState<SettlementPayeeListStatus>("default");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [regionFilter, setRegionFilter] =
    useState<AdminRegionFilterValue>(null);
  /** Any filter change starts the list again from page 1. */
  const withFirstPage =
    <V,>(apply: (value: V) => void) =>
    (value: V) => {
      apply(value);
      setPage(1);
    };
  const changeRole = withFirstPage(setRoleFilter);
  const changeStatus = withFirstPage(setStatusFilter);
  const changeSearch = withFirstPage(setSearch);
  const changeRegion = withFirstPage(setRegionFilter);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    isPlaceholderData,
  } = useQuery({
    queryKey: adminSettlementKeys.payeeList({
      role: roleFilter,
      status: statusFilter,
      search: debouncedSearch,
      regionId: regionFilter,
      page,
    }),
    queryFn: () =>
      adminSettlementService.listPayees({
        role: roleFilter || undefined,
        search: debouncedSearch || undefined,
        has_open_lines: true,
        ...settlementPayeeStatusParams(statusFilter),
        region_id: regionFilter ?? undefined,
        skip: pageSkip(page, PAGE_SIZE),
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  useClampUrlPage(
    page,
    data && !isPlaceholderData
      ? serverPageWindow(page, PAGE_SIZE, total).totalPages
      : undefined,
  );
  const totals = data?.totals;

  const kpiTiles: {
    key: string;
    title: string;
    cents: (totals: SettlementPayeeTotals) => number;
    payeeCount: (totals: SettlementPayeeTotals) => number;
    icon: LucideIcon;
    iconColor: IconTileTone;
    filter?: SettlementPayeeListStatus;
  }[] = [
    {
      key: "payable",
      title: t("settlements.detail.openPayable"),
      cents: (entry) => entry.open_payable_cents,
      payeeCount: (entry) => entry.payable_payee_count,
      icon: Banknote,
      iconColor: "primary",
      filter: "to_be_paid",
    },
    {
      key: "invoiceable",
      title: t("settlements.detail.openInvoiceable"),
      cents: (entry) => entry.open_invoiceable_cents,
      payeeCount: (entry) => entry.invoiceable_payee_count,
      icon: FileText,
      iconColor: "accent",
      filter: "to_be_invoiced",
    },
    {
      key: "collectible",
      title: t("settlements.detail.openCollectible"),
      cents: (entry) => entry.open_collectible_cents,
      payeeCount: (entry) => entry.collectible_payee_count,
      icon: HandCoins,
      iconColor: "warning",
    },
    {
      key: "supplier",
      title: t("settlements.detail.openPendingSupplierInvoice"),
      cents: (entry) => entry.open_pending_supplier_invoice_cents,
      payeeCount: (entry) => entry.pending_supplier_invoice_payee_count,
      icon: Receipt,
      iconColor: "success",
    },
  ];

  const formatAmountWithCount = useCallback(
    (cents: number, currency: string, count: number) => {
      if (count <= 0) {
        return t("shared.emptyCell");
      }
      return t("settlements.payees.amountWithCount", {
        amount: formatMoneyOrEmpty(cents, currency, t("shared.emptyCell")),
        count,
      });
    },
    [t],
  );

  const statusOptions: { value: SettlementPayeeListStatus; label: string }[] = [
    { value: "default", label: t("settlements.payees.filterDefault") },
    { value: "to_be_paid", label: t("settlements.payees.filterToBePaid") },
    {
      value: "to_be_invoiced",
      label: t("settlements.payees.filterToBeInvoiced"),
    },
  ];

  const columns = useMemo<
    DataTableColumn<SettlementPayeeRow, ServerOrderColumn>[]
  >(
    () => [
      {
        id: "name",
        header: t("settlements.payees.colName"),
        defaultWidth: 220,
        cell: (payee) => (
          <div className="flex min-w-0 items-center gap-3">
            <span className="relative shrink-0">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-xs font-semibold text-primary-700 ring-1 ring-inset ring-primary-600/15"
                aria-hidden
              >
                {getInitials(payee.name) || "?"}
              </span>
              {!payee.has_legal_info && (
                <LegalIncompleteBadge className="absolute -right-1.5 -top-1.5 h-4 w-4 text-[10px] outline-2 outline-surface-0" />
              )}
            </span>
            <div className="min-w-0">
              <DataTablePrimaryText>{payee.name}</DataTablePrimaryText>
              <div className="break-words text-xs text-surface-500">
                {payee.email}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "role",
        header: t("settlements.payees.colRole"),
        defaultWidth: 120,
        cell: (payee) => (
          <span className="break-words text-surface-700">
            {settlementRoleLabel(payee.role, t)}
          </span>
        ),
      },
      {
        id: "region",
        header: tCommon("region.colCode"),
        defaultWidth: 100,
        cell: (payee) => <RegionCodeBadge code={payee.region_code} />,
      },
      {
        id: "payable",
        header: t("settlements.payees.colPayable"),
        defaultWidth: 140,
        cell: (payee) => (
          <span className="break-words font-medium tabular-nums text-surface-900">
            {formatAmountWithCount(
              payee.open_payable_cents,
              payee.currency,
              payee.unpaid_count,
            )}
          </span>
        ),
      },
      {
        id: "commissionOpen",
        header: t("settlements.payees.colCommissionOpen"),
        defaultWidth: 140,
        cell: (payee) => (
          <span className="break-words font-medium tabular-nums text-surface-900">
            {formatAmountWithCount(
              payee.open_invoiceable_cents,
              payee.currency,
              payee.uninvoiced_count,
            )}
          </span>
        ),
      },
      {
        id: "collectible",
        header: t("settlements.detail.openCollectible"),
        defaultWidth: 140,
        cell: (payee) => (
          <span className="break-words font-medium tabular-nums text-surface-900">
            {formatAmountWithCount(
              payee.open_collectible_cents,
              payee.currency,
              payee.uncollected_count,
            )}
          </span>
        ),
      },
      {
        id: "supplierOpen",
        header: t("settlements.payees.colSupplierOpen"),
        defaultWidth: 160,
        cell: (payee) => (
          <span className="break-words font-medium tabular-nums text-surface-900">
            {formatAmountWithCount(
              payee.open_pending_supplier_invoice_cents,
              payee.currency,
              payee.pending_supplier_invoice_count,
            )}
          </span>
        ),
      },
    ],
    [formatAmountWithCount, t, tCommon],
  );

  return (
    <PageFrame
      title={t("layout.tabSettlements")}
      description={t("layout.settlementsDescription")}
      icon={HandCoins}
      actions={
        <Button
          type="button"
          variant="outline"
          isLoading={isFetching}
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={() => void refetch()}
        >
          {tCommon("refresh")}
        </Button>
      }
    >
      <div className="space-y-4">
        <StatCardGrid layout="1-2-4" className="grid-cols-2">
          {kpiTiles.map((tile) => {
            const value =
              totals && totals.length > 0
                ? totals
                    .map((entry) => formatMoney(tile.cents(entry), entry.currency))
                    .join(" · ")
                : t("shared.emptyCell");
            const subtitle = totals
              ? t("settlements.payees.kpiPayeeCount", {
                  count: totals.reduce(
                    (acc, entry) => acc + tile.payeeCount(entry),
                    0,
                  ),
                })
              : undefined;
            const card = (
              <StatCard
                title={tile.title}
                value={value}
                subtitle={subtitle}
                loading={isLoading}
                icon={tile.icon}
                iconColor={tile.iconColor}
                selected={tile.filter !== undefined && statusFilter === tile.filter}
                className={tile.filter ? "cursor-pointer" : undefined}
              />
            );
            if (!tile.filter) {
              return <div key={tile.key}>{card}</div>;
            }
            const tileFilter = tile.filter;
            return (
              <button
                key={tile.key}
                type="button"
                aria-pressed={statusFilter === tileFilter}
                onClick={() =>
                  changeStatus(
                    statusFilter === tileFilter ? "default" : tileFilter,
                  )
                }
                className={selectableStatCardButtonClass}
              >
                {card}
              </button>
            );
          })}
        </StatCardGrid>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <SearchField
            value={search}
            onChange={changeSearch}
            placeholder={t("settlements.payees.searchPlaceholder")}
            searchLabel={tCommon("search")}
            clearSearchLabel={tCommon("clearSearch")}
            className="min-w-0 max-w-none sm:min-w-[240px] sm:flex-1 lg:max-w-sm"
          />
          <FilterBar>
            <AdminFilterMenu<RoleFilter>
              icon={Users}
              name={t("settlements.payees.filterRole")}
              value={roleFilter}
              defaultValue=""
              onChange={changeRole}
              options={[
                {
                  value: "",
                  label: t("settlements.payees.filterAllRoles"),
                },
                {
                  value: UserRole.SELLER,
                  label: t("settlements.roles.seller"),
                  icon: ROLE_ICON_CONFIG.seller.icon,
                  iconBadgeClassName: ROLE_ICON_CONFIG.seller.className,
                },
                {
                  value: UserRole.HOST,
                  label: t("settlements.roles.host"),
                  icon: ROLE_ICON_CONFIG.host.icon,
                  iconBadgeClassName: ROLE_ICON_CONFIG.host.className,
                },
                {
                  value: UserRole.SALES_AGENT,
                  label: t("settlements.roles.salesAgent"),
                  icon: ROLE_ICON_CONFIG["sales-agent"].icon,
                  iconBadgeClassName:
                    ROLE_ICON_CONFIG["sales-agent"].className,
                },
              ]}
            />
            <AdminFilterMenu<SettlementPayeeListStatus>
              icon={CircleDot}
              name={t("shared.colStatus")}
              value={statusFilter}
              defaultValue="default"
              onChange={changeStatus}
              options={statusOptions}
            />
            <AdminRegionFilter
              value={regionFilter}
              onChange={changeRegion}
              valueKey="id"
            />
          </FilterBar>
        </div>

        <DataTable
          labels={tableLabels}
          closeLabel={tCommon("close")}
          loadingLabel={tCommon("loading")}
          rows={items}
          columns={columns}
          getRowKey={(row) => row.user_id}
          defaultSortColumn="name"
          serverPagination={{
            page,
            pageSize: PAGE_SIZE,
            total,
            onPageChange: setPage,
            isFetching,
          }}
          preferencesKey="admin-settlement-payees"
          loadState={queryLoadState({
            isLoading,
            error,
            errorMessage: t("settlements.payees.loadError"),
            retryLabel: tCommon("tryAgain"),
            onRetry: () => void refetch(),
          })}
          emptyMessage={t("settlements.payees.empty")}
          onRowClick={(payee) =>
            void navigate(`/admin/payments/settlements/${payee.user_id}`)
          }
          openRowLabel={(payee) =>
            t("settlements.payees.openRowAria", { name: payee.name })
          }
        />
      </div>
    </PageFrame>
  );
}
